import { useState, useEffect } from 'react';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { 
  Plus, ChevronLeft, ChevronRight, Edit, Trash2,
  Tag, ChevronUp, ChevronDown, ChevronsUpDown
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from 'sonner';

const initialFormData = {
  tipo: '',
  description: '',
};

const TiposLicenciaPage = () => {
  const { canCreate, canUpdate, canDelete } = usePermissions('config', 'tipos_licencia');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, page_size: 25, total: 0, pages: 0 });
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ field: 'tipo', dir: 'asc' });
  
  // Filters
  const [searchFilter, setSearchFilter] = useState('');
  
  // Modals
  const [editModal, setEditModal] = useState({ open: false, item: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.page_size.toString(),
      });
      
      if (searchFilter) params.append('search', searchFilter);

      const response = await api.get(`/config/tipos-licencia?${params}`);
      setItems(response.data.items);
      setPagination(prev => ({ ...prev, total: response.data.total, pages: response.data.pages }));
    } catch (error) {
      toast.error('Error al cargar tipos de licencia');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchItems();
  }, [pagination.page, sortConfig]);
  
  // Debounced filter effect
  useEffect(() => {
    const t = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchItems();
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilter]);

  // CRUD handlers
  const handleCreate = () => {
    setFormData(initialFormData);
    setEditModal({ open: true, item: null });
  };

  const handleEdit = (item) => {
    setFormData({
      tipo: item.tipo || '',
      description: item.description || '',
    });
    setEditModal({ open: true, item });
  };

  const handleSave = async () => {
    if (!formData.tipo.trim()) {
      toast.error('El tipo es requerido');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        tipo: formData.tipo.trim(),
        description: formData.description || null,
      };
      
      if (editModal.item) {
        await api.put(`/config/tipos-licencia/${editModal.item.id}`, payload);
        toast.success('Tipo actualizado');
      } else {
        await api.post('/config/tipos-licencia', payload);
        toast.success('Tipo creado');
      }
      setEditModal({ open: false, item: null });
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/config/tipos-licencia/${deleteModal.item.id}`);
      toast.success('Tipo eliminado');
      setDeleteModal({ open: false, item: null });
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Tipos de Licencia</h1>
            <p className="page-subtitle">Catalogo de tipos de licencia (Perpetua, Suscripcion, etc.)</p>
          </div>
          {canCreate && (
            <Button onClick={handleCreate} className="btn btn-primary" data-testid="new-tipo-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Tipo
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 gap-4" data-testid="tipos-licencia-stats">
          <div className="stats-card" data-testid="stats-total">
            <div className="mb-2">
              <Tag className="h-8 w-8 text-primary" strokeWidth={1.5} />
            </div>
            <div className="stats-card-value">{pagination.total}</div>
            <div className="stats-card-label">Total Tipos</div>
          </div>
        </div>

        {/* Table */}
        <Card className="card-subtle overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="table-subtle">
                    <thead>
                      <tr>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('tipo')}
                        >
                          <div className="flex items-center gap-1">
                            TIPO
                            <SortIcon field="tipo" />
                          </div>
                        </th>
                        <th>DESCRIPCION</th>
                        <th className="w-20 text-center">ACCIONES</th>
                      </tr>
                      {/* Filters Row */}
                      <tr className="bg-muted/30">
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Buscar..."
                            value={searchFilter}
                            onChange={(e) => setSearchFilter(e.target.value)}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-search"
                          />
                        </th>
                        <th className="py-2 px-4"></th>
                        <th className="w-20"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan={3}>
                            <div className="empty-state">
                              <Tag className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                              <p className="empty-state-text">No se encontraron tipos</p>
                            </div>
                          </td>
                        </tr>
                      ) : items.map((item) => (
                        <tr key={item.id} data-testid={`tipo-row-${item.id}`}>
                          <td className="font-medium text-primary">{item.tipo}</td>
                          <td className="text-muted-foreground text-sm max-w-[300px] truncate">
                            {item.description || '-'}
                          </td>
                          <td className="w-20">
                            <div className="flex justify-center gap-0.5">
                              <button
                                onClick={() => canUpdate && handleEdit(item)}
                                className={`action-btn action-btn-edit ${!canUpdate ? 'opacity-50' : ''}`}
                                disabled={!canUpdate}
                                title="Editar"
                                data-testid={`edit-tipo-${item.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canDelete && setDeleteModal({ open: true, item })}
                                className={`action-btn action-btn-delete ${!canDelete ? 'opacity-50' : ''}`}
                                disabled={!canDelete}
                                title="Eliminar"
                                data-testid={`delete-tipo-${item.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <div className="text-sm text-muted-foreground">
                    {pagination.total > 0 ? `${(pagination.page - 1) * pagination.page_size + 1} - ${Math.min(pagination.page * pagination.page_size, pagination.total)} de ${pagination.total}` : '0 registros'}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(p => ({ ...p, page: Math.max(1, p.page - 1) }))}
                      disabled={pagination.page === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">{pagination.page} / {pagination.pages || 1}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(p => ({ ...p, page: Math.min(p.pages, p.page + 1) }))}
                      disabled={pagination.page >= pagination.pages}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Edit/Create Modal */}
        <Dialog open={editModal.open} onOpenChange={(open) => setEditModal({ open, item: null })}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Tag className="h-5 w-5 text-primary" />
                {editModal.item ? 'Editar Tipo' : 'Nuevo Tipo'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-1">
                <Label className="text-xs">Tipo *</Label>
                <Input
                  value={formData.tipo}
                  onChange={(e) => setFormData(prev => ({ ...prev, tipo: e.target.value }))}
                  placeholder="Ej: Perpetua, Suscripcion, OEM"
                  data-testid="input-tipo"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Descripcion</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descripcion opcional"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModal({ open: false, item: null })}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="btn btn-primary">
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, item: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Eliminar Tipo
              </DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground">
              Estas seguro que deseas eliminar el tipo <strong>{deleteModal.item?.tipo}</strong>?
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModal({ open: false, item: null })}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TiposLicenciaPage;
